// Practice topics: the filters a learner can put on a game (काल, विभक्ति, वचन, पुरुष, कृदन्त, सन्धि, समास).
// Shared by the browser and the server: ids, Hindi + English labels, which game understands which topic, and the
// `topic` query parameter (comma-separated ids, e.g. ?topic=bhoot,bahu). Inside one group the chosen topics are
// alternatives (वर्तमान or भूत); different groups narrow each other (भूत and बहुवचन). A game uses the chosen topics
// it understands and ignores the rest; a game that understands none of them is shown dimmed on the hub.
// Next to the topics sits the विकल्प रूप choice (`vikalp=1`: variant forms included; absent: standard forms only, the
// default). It is not a topic: it never narrows the questions, and the games without inflected answers ignore it.

import type { L } from '@/prakrit/lib/lessons/types'
import type { Case, Num, Person } from '@/prakrit/lib/grammar/types'
import type { SamasType } from '@/prakrit/lib/content/grammar-examples'
import type { PracticeMode, SessionMode } from '@/prakrit/components/practice/practice-types'

import { SAMAS_TYPES } from '@/prakrit/lib/content/grammar-examples'
import { CASES, NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'

import {
    CASE_LABEL,
    KRIDANTA_LABEL,
    NUMBER_LABEL,
    TENSE_LABEL,
    TENSE_SHORT
} from '@/prakrit/lib/grammar/labels'

export type TopicGroup = 'kaal' | 'vibhakti' | 'vachan' | 'purush' | 'kridanta' | 'sandhi' | 'samas'

/** The tenses a practice game can ask: the four taught first, then three Adults topics. */
export const PRACTICE_TENSES = ['vartaman', 'vidhi', 'bhoot', 'bhavishya', 'karmani_vartaman', 'prerak_vartaman', 'kriyatipatti'] as const

export type PracticeTense = typeof PRACTICE_TENSES[number]

export const BASIC_PRACTICE_TENSES: PracticeTense[] = ['vartaman', 'vidhi', 'bhoot', 'bhavishya']

/** The कृदन्त kinds the practice games ask (the engine generates these for अ-roots; आ-roots have the first two). */
export const PRACTICE_KRIDANTA = ['sambandh', 'hetvarth', 'vartaman', 'bhoot', 'bhavishya', 'vidhi'] as const

export type PracticeKridanta = typeof PRACTICE_KRIDANTA[number]

export const SANDHI_GROUPS = ['svar', 'avyaya', 'prakriti', 'anusvar'] as const

export type SandhiGroup = typeof SANDHI_GROUPS[number]

const SAMAS_ORDER: SamasType[] = ['avyayibhav', 'tatpurush', 'karmadharay', 'dvigu', 'dvandva', 'bahuvrihi']

export interface TopicInfo {
    /** The id used in the URL (?topic=…). */
    id: string
    group: TopicGroup
    /** The grammar value behind the topic: a tense, case, number, person, कृदन्त kind, sandhi group or samāsa type. */
    value: string
    /** Short label, shown inside its group on the hub. */
    label: L
    /** Full label, shown on its own (the active topics of a session). */
    full: L
    /** Shown in Kids mode too (the four tenses and वचन). */
    kids: boolean
}

const PERSON_TOPIC: Record<Person, { label: L, full: L }> = {
    uttam: { label: { hi: 'उत्तम', en: 'I, we' }, full: { hi: 'उत्तम पुरुष', en: 'speaker (I, we)' } },
    madhyam: { label: { hi: 'मध्यम', en: 'you' }, full: { hi: 'मध्यम पुरुष', en: 'person addressed (you)' } },
    anya: { label: { hi: 'अन्य', en: 'he, she, they' }, full: { hi: 'अन्य पुरुष', en: 'person spoken about (he, she, they)' } }
}

const KRIDANTA_SHORT: Record<PracticeKridanta, string> = {
    sambandh: 'सम्बन्ध',
    hetvarth: 'हेत्वर्थ',
    vartaman: 'वर्तमान',
    bhoot: 'भूत',
    bhavishya: 'भविष्य',
    vidhi: 'विधि'
}

const SANDHI_TOPIC: Record<SandhiGroup, { label: L, full: L }> = {
    svar: { label: { hi: 'स्वर सन्धि', en: 'vowels' }, full: { hi: 'स्वर सन्धि', en: 'vowel sandhi' } },
    avyaya: { label: { hi: 'अव्यय सन्धि', en: 'अपि, इति, इव' }, full: { hi: 'अव्यय सन्धि', en: 'sandhi with अपि, इति, इव' } },
    prakriti: { label: { hi: 'प्रकृतिभाव', en: 'no sandhi' }, full: { hi: 'प्रकृतिभाव', en: 'no sandhi after a verb' } },
    anusvar: { label: { hi: 'अनुस्वार', en: 'final म्' }, full: { hi: 'अनुस्वार सन्धि', en: 'final म् becomes anusvāra' } }
}

export const TOPICS: TopicInfo[] = [
    ...PRACTICE_TENSES.map((t): TopicInfo => ({
        id: t,
        group: 'kaal',
        value: t,
        label: { hi: TENSE_SHORT[t].hi, en: TENSE_SHORT[t].en },
        full: { hi: TENSE_LABEL[t].hi, en: TENSE_LABEL[t].en },
        kids: BASIC_PRACTICE_TENSES.includes(t)
    })),
    ...CASES.map((c): TopicInfo => ({
        id: c,
        group: 'vibhakti',
        value: c,
        label: { hi: CASE_LABEL[c].hi, en: CASE_LABEL[c].en },
        full: { hi: `${CASE_LABEL[c].hi} विभक्ति`, en: `${CASE_LABEL[c].en} case` },
        kids: false
    })),
    ...NUMBERS.map((n): TopicInfo => ({
        id: n,
        group: 'vachan',
        value: n,
        label: { hi: NUMBER_LABEL[n].hi, en: NUMBER_LABEL[n].en },
        full: { hi: NUMBER_LABEL[n].hi, en: NUMBER_LABEL[n].en },
        kids: true
    })),
    ...PERSONS.map((p): TopicInfo => ({ id: p, group: 'purush', value: p, ...PERSON_TOPIC[p], kids: false })),
    ...PRACTICE_KRIDANTA.map((k): TopicInfo => ({
        id: `kr-${k}`,
        group: 'kridanta',
        value: k,
        label: { hi: KRIDANTA_SHORT[k], en: KRIDANTA_LABEL[k].en },
        full: { hi: KRIDANTA_LABEL[k].hi, en: `participle: ${KRIDANTA_LABEL[k].en}` },
        kids: false
    })),
    ...SANDHI_GROUPS.map((g): TopicInfo => ({ id: `sandhi-${g}`, group: 'sandhi', value: g, ...SANDHI_TOPIC[g], kids: false })),
    ...SAMAS_ORDER.map((t): TopicInfo => ({
        id: `samas-${t}`,
        group: 'samas',
        value: t,
        label: { hi: SAMAS_TYPES[t].hi, en: SAMAS_TYPES[t].en },
        full: { hi: `${SAMAS_TYPES[t].hi} समास`, en: `${SAMAS_TYPES[t].en} compound` },
        kids: false
    }))
]

export const TOPIC_BY_ID: Record<string, TopicInfo> = Object.fromEntries(TOPICS.map(t => [t.id, t]))

export const TOPIC_GROUPS: { id: TopicGroup, title: L, kids: boolean, more: boolean }[] = [
    { id: 'kaal', title: { hi: 'काल', en: 'Tense' }, kids: true, more: false },
    { id: 'vibhakti', title: { hi: 'विभक्ति', en: 'Case' }, kids: false, more: false },
    { id: 'vachan', title: { hi: 'वचन', en: 'Number' }, kids: true, more: false },
    { id: 'purush', title: { hi: 'पुरुष', en: 'Person' }, kids: false, more: false },
    { id: 'kridanta', title: { hi: 'कृदन्त', en: 'Participles' }, kids: false, more: false },
    { id: 'sandhi', title: { hi: 'सन्धि', en: 'Sandhi' }, kids: false, more: true },
    { id: 'samas', title: { hi: 'समास', en: 'Compounds' }, kids: false, more: true }
]

const ids = (group: TopicGroup, values?: readonly string[]): string[] => TOPICS
    .filter(t => t.group === group && (!values || values.includes(t.value)))
    .map(t => t.id)

/** रूप साधना leaves out भूत and क्रियातिपत्ति: their six cells are one form, so a hidden cell would be given away. */
const TABLE_TENSES: PracticeTense[] = ['vartaman', 'vidhi', 'bhavishya', 'karmani_vartaman', 'prerak_vartaman']

/** Which topics each game understands. The sentence games have author-written meanings for the four basic tenses only. */
export const MODE_TOPICS: Record<PracticeMode, string[]> = {
    'dhatu-chunauti': [...ids('kaal'), ...ids('vachan'), ...ids('purush')],
    'kaal-pahchan': [...ids('kaal'), ...ids('vachan'), ...ids('purush')],
    'roop-sadhana': [...ids('kaal', TABLE_TENSES), ...ids('vachan'), ...ids('purush')],
    'kridanta-pahchan': ids('kridanta'),
    'vibhakti-chayan': [...ids('vibhakti'), ...ids('vachan')],
    'vachan-badlo': [...ids('kaal', BASIC_PRACTICE_TENSES), ...ids('vachan'), ...ids('purush')],
    'vakya-nirmata': [...ids('kaal', BASIC_PRACTICE_TENSES), ...ids('vachan'), ...ids('purush')],
    'anuvad': [...ids('kaal', BASIC_PRACTICE_TENSES), ...ids('vachan'), ...ids('purush')],
    'sanskrit-prakrit': [],
    'sandhi-nirmata': ids('sandhi'),
    'samas-pahchan': ids('samas'),
    'shravan': [],
    'katha-kram': []
}

const TABLE_CELLS_NOTE: L = { hi: 'वचन और पुरुष = कौन-से खाने खाली होंगे', en: 'number and person = which cells are left empty' }

/** What a topic means in one game, when that game reads it differently from the label. */
export const TOPIC_NOTE: Partial<Record<PracticeMode, Partial<Record<TopicGroup, L>>>> = {
    'vachan-badlo': { vachan: { hi: 'वचन = जिस वचन में बदलना है', en: 'number = the number to change into' } },
    'roop-sadhana': { vachan: TABLE_CELLS_NOTE, purush: TABLE_CELLS_NOTE },
    'kaal-pahchan': { kaal: { hi: 'काल = सही उत्तर का काल', en: 'tense = the tense of the right answer' } }
}

/** Valid topic ids from a query value ("bhoot,bahu"), without repeats, in the catalogue order. */
export const parseTopics = (raw: unknown): string[] => {
    const text = Array.isArray(raw) ? raw.join(',') : typeof raw === 'string' ? raw : ''
    const wanted = new Set(text.slice(0, 600).split(',').map(s => s.trim()).filter(Boolean))

    return TOPICS.filter(t => wanted.has(t.id)).map(t => t.id)
}

/** The chosen topics this game understands. */
export const topicsForMode = (mode: PracticeMode, topics: readonly string[]): string[] => {
    const known = MODE_TOPICS[mode]

    return topics.filter(id => known.includes(id))
}

/** The `topic` query value, or an empty string. */
export const topicQuery = (topics: readonly string[]): string => topics.join(',')

// ---------------------------------------------------------------- विकल्प रूप (variant forms)

/** The query parameter of the विकल्प choice: `vikalp=1` includes variant forms; without it only standard forms. */
export const VIKALP_PARAM = 'vikalp'

/** The games whose answers or cues are inflected forms, so a विकल्प (variant) form can stand in them. */
export const VIKALP_MODES: PracticeMode[] = [
    'dhatu-chunauti',
    'kaal-pahchan',
    'roop-sadhana',
    'kridanta-pahchan',
    'vibhakti-chayan',
    'vachan-badlo',
    'vakya-nirmata',
    'anuvad'
]

/** Does this game read the विकल्प choice? (आज का अभ्यास passes it on to the games it draws from.) */
export const usesVikalp = (mode: SessionMode): boolean => mode === 'aaj' || VIKALP_MODES.includes(mode)

/** The विकल्प choice from a query value: only `1` (or `true`) includes variant forms. */
export const parseVikalp = (raw: unknown): boolean => {
    const value = Array.isArray(raw) ? raw[0] : raw

    return value === '1' || value === 'true'
}

/** The two choices of the filter bar, standard forms only (the default) first. */
export const VIKALP_CHOICES: { on: boolean, label: L }[] = [
    { on: false, label: { hi: 'विकल्प रहित', en: 'Exclude variants' } },
    { on: true, label: { hi: 'विकल्प सहित', en: 'Include variants' } }
]

export const VIKALP_TITLE: L = { hi: 'विकल्प रूप', en: 'Variant forms' }

/** A practice link with the level, the topics this game understands and (optionally) the विकल्प choice. */
export const practiceHref = (mode: PracticeMode | 'aaj', level: string, topics: readonly string[] = [], vikalp = false): string => {
    const applied = mode === 'aaj' ? [] : topicsForMode(mode, topics)
    const params = [`level=${level}`]

    if (applied.length > 0) params.push(`topic=${topicQuery(applied)}`)
    if (vikalp && usesVikalp(mode)) params.push(`${VIKALP_PARAM}=1`)

    return `/practice/${mode}?${params.join('&')}`
}

/** The practice hub with the chosen topics and the विकल्प choice, so going back keeps them. */
export const practiceHubHref = (topics: readonly string[] = [], vikalp = false): string => {
    const params = [
        ...(topics.length > 0 ? [`topic=${topicQuery(topics)}`] : []),
        ...(vikalp ? [`${VIKALP_PARAM}=1`] : [])
    ]

    return params.length > 0 ? `/practice?${params.join('&')}` : '/practice'
}

export interface TopicSelection {
    tenses: PracticeTense[] | null
    cases: Case[] | null
    numbers: Num[] | null
    persons: Person[] | null
    kridanta: PracticeKridanta[] | null
    sandhi: SandhiGroup[] | null
    samas: SamasType[] | null
}

/** No filter: every group open. */
export const NO_TOPICS: TopicSelection = {
    tenses: null,
    cases: null,
    numbers: null,
    persons: null,
    kridanta: null,
    sandhi: null,
    samas: null
}

/** The chosen topics a game understands, by group (null: that group is not filtered). */
export const topicSelection = (mode: PracticeMode, topics: readonly string[]): TopicSelection => {
    const applied = topicsForMode(mode, topics).map(id => TOPIC_BY_ID[id])

    const of = <T extends string>(group: TopicGroup): T[] | null => {
        const values = applied.filter(t => t.group === group).map(t => t.value as T)

        return values.length > 0 ? values : null
    }

    return {
        tenses: of<PracticeTense>('kaal'),
        cases: of<Case>('vibhakti'),
        numbers: of<Num>('vachan'),
        persons: of<Person>('purush'),
        kridanta: of<PracticeKridanta>('kridanta'),
        sandhi: of<SandhiGroup>('sandhi'),
        samas: of<SamasType>('samas')
    }
}

export const isFiltered = (sel: TopicSelection): boolean => Object.values(sel).some(v => v !== null)
